import { useCallback, useEffect, useState } from 'react'
import * as stylex from '@stylexjs/stylex'

import Avatar from '../../components/Avatar'
import Button from '../../components/Button'
import Card from '../../components/Card'
import EmptyState from '../../components/EmptyState'
import PageHeader from '../../components/PageHeader'
import Icon from '../../components/Icon'
import { formatMoney, findPerson, type CreateLoanInput, type Loan, type Overview, v1API } from '../../lib/ledger'
import { LoanForm, PaymentForm, type LoanWithBalance } from './LoansPage'
import { mobileStyles as styles } from '../mobileStyles'

const loanStyles = stylex.create({
  status: { borderRadius: '9999px', paddingInline: '8px', paddingBlock: '4px', fontSize: '10px', fontWeight: 600 },
  open: { backgroundColor: '#eef4ff', color: '#426b9d' },
  overdue: { backgroundColor: '#fff1eb', color: '#a44e20' },
  paid: { backgroundColor: '#edf7ef', color: '#347a47' },
  cancelled: { backgroundColor: '#f1f2ef', color: '#68746b' },
  metricRow: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '10px' },
  metric: { borderRadius: '0.9rem', borderWidth: 1, borderColor: '#d9e8d8', backgroundColor: '#f5fbf3', padding: '13px' },
  metricLabel: { color: '#66806c', fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em' },
  metricValue: { marginTop: '5px', color: '#2d7342', fontSize: '18px', fontWeight: 650 },
  loanGrid: { display: 'grid', gap: '16px', '@media (min-width: 768px)': { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' } },
  loanHeader: { display: 'flex', alignItems: 'start', justifyContent: 'space-between', gap: '10px' },
  loanPerson: { display: 'flex', minWidth: '0px', alignItems: 'center', gap: '10px' },
  loanName: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '13px', fontWeight: 650 },
  loanMeta: { marginTop: '3px', color: '#78847b', fontSize: '11px' },
  outstanding: { marginTop: '16px', display: 'flex', flexWrap: 'wrap', alignItems: 'end', justifyContent: 'space-between', gap: '10px' },
  outstandingLabel: { color: '#778379', fontSize: '11px' },
  outstandingValue: { marginTop: '3px', fontSize: '22px', fontWeight: 650, overflowWrap: 'anywhere' },
  due: { textAlign: 'right', color: '#78847b', fontSize: '10px', lineHeight: '1.1rem' },
  repay: { marginTop: '14px', display: 'flex', minHeight: '44px', width: '100%', alignItems: 'center', justifyContent: 'center', gap: '7px', borderRadius: '0.75rem', backgroundColor: '#eef6eb', color: '#397b49', fontSize: '12px', fontWeight: 650, ':hover': { backgroundColor: '#e2f0df' } },
  retry: { marginTop: '14px' },
})

type LoansMobilePageProps = { data: Overview; previewLoans?: LoanWithBalance[] }

function statusLabel(status: Loan['status']) {
  return status === 'OVERDUE' ? 'Overdue' : status.charAt(0) + status.slice(1).toLowerCase()
}

function statusStyle(status: Loan['status']) {
  if (status === 'PAID') return loanStyles.paid
  if (status === 'OVERDUE') return loanStyles.overdue
  if (status === 'CANCELLED') return loanStyles.cancelled
  return loanStyles.open
}

export default function LoansMobilePage({ data, previewLoans }: LoansMobilePageProps) {
  const [items, setItems] = useState<LoanWithBalance[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showCreate, setShowCreate] = useState(false)
  const [paying, setPaying] = useState<LoanWithBalance | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const loans = (await v1API.listLoans()) ?? []
      setItems(await Promise.all(loans.map(async (loan) => ({ loan, balance: await v1API.getLoanBalance(loan.id) }))))
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Could not load loans.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (previewLoans) {
      const timeout = window.setTimeout(() => { setItems(previewLoans); setLoading(false) }, 0)
      return () => window.clearTimeout(timeout)
    }
    const timeout = window.setTimeout(() => { void load() }, 0)
    return () => window.clearTimeout(timeout)
  }, [load, previewLoans])

  async function createLoan(input: CreateLoanInput) {
    await v1API.createLoan(input)
    setShowCreate(false)
    await load()
  }

  async function createPayment(loanID: string, amountCents: number) {
    await v1API.createLoanPayment(loanID, { amountCents, method: 'manual' })
    setPaying(null)
    await load()
  }

  const active = items.filter((item) => item.loan.status === 'ACTIVE' || item.loan.status === 'OVERDUE')
  const outstanding = active.reduce((sum, item) => sum + item.balance.outstandingCents, 0)
  const currencies = new Set(active.map((item) => item.loan.currency))
  const displayCurrency = currencies.size === 1 ? [...currencies][0] : data.currentUser.currency

  return <div {...stylex.props(styles.page)}>
    <PageHeader action={<Button aria-label="Create a loan" onClick={() => setShowCreate(true)}><Icon name="plus" />New loan</Button>} eyebrow="Borrowing, made clear" subtitle="Track principal, fees, and repayments from your phone." title="Loans" />
    <section {...stylex.props(loanStyles.metricRow)}><div {...stylex.props(loanStyles.metric)}><p {...stylex.props(loanStyles.metricLabel)}>Open loans</p><p {...stylex.props(loanStyles.metricValue)}>{active.length}</p></div><div {...stylex.props(loanStyles.metric)}><p {...stylex.props(loanStyles.metricLabel)}>Outstanding</p><p {...stylex.props(loanStyles.metricValue)}>{currencies.size === 1 ? formatMoney(outstanding, displayCurrency) : `${currencies.size} currencies`}</p></div></section>
    {loading ? <Card><EmptyState description="Loading loans…" /></Card> : error ? <Card><EmptyState action={<Button onClick={() => void load()} variant="secondary">Try again</Button>} description={error} title="We could not load your loans" /></Card> : active.length === 0 ? <Card><EmptyState action={<Button onClick={() => setShowCreate(true)}>Create your first loan</Button>} description="Create a loan when money changes hands." icon={<Icon name="loans" />} title="No open loans yet" /></Card> : <section {...stylex.props(loanStyles.loanGrid)}>{active.map((item) => <LoanMobileCard data={data} item={item} key={item.loan.id} onPay={() => setPaying(item)} />)}</section>}
    {showCreate && <LoanForm data={data} onClose={() => setShowCreate(false)} onSave={createLoan} />}
    {paying && <PaymentForm item={paying} onClose={() => setPaying(null)} onSave={createPayment} />}
  </div>
}

function LoanMobileCard({ data, item, onPay }: { data: Overview; item: LoanWithBalance; onPay: () => void }) {
  const borrower = findPerson(data, item.loan.borrowerPersonId)
  const lender = findPerson(data, item.loan.lenderPersonId)
  const otherID = item.loan.lenderPersonId === data.currentUser.id ? item.loan.borrowerPersonId : item.loan.lenderPersonId
  return <article {...stylex.props(styles.card)}>
    <div {...stylex.props(loanStyles.loanHeader)}><div {...stylex.props(loanStyles.loanPerson)}><Avatar person={borrower ?? { id: item.loan.borrowerPersonId, name: 'Borrower', color: 1 }} size={40} /><div {...stylex.props(styles.listMain)}><p {...stylex.props(loanStyles.loanName)}>{borrower?.name ?? 'Borrower'}</p><p {...stylex.props(loanStyles.loanMeta)}>Lent by {lender?.name ?? 'you'}</p></div></div><span {...stylex.props(loanStyles.status, statusStyle(item.loan.status))}>{statusLabel(item.loan.status)}</span></div>
    <div {...stylex.props(loanStyles.outstanding)}><div><p {...stylex.props(loanStyles.outstandingLabel)}>Outstanding</p><p {...stylex.props(loanStyles.outstandingValue)}>{formatMoney(item.balance.outstandingCents, item.loan.currency)}</p></div><p {...stylex.props(loanStyles.due)}>{item.loan.monthlyInterestCents ? `${formatMoney(item.loan.monthlyInterestCents, item.loan.currency)} / month` : `${(item.loan.monthlyInterestBps / 100).toFixed(2)}% / month`}<br />{item.loan.dueAt ? `Due ${item.loan.dueAt.slice(0, 10)}` : 'No due date'}</p></div>
    <p {...stylex.props(loanStyles.loanMeta)}>Principal {formatMoney(item.balance.principalOutstandingCents, item.loan.currency)} · interest {formatMoney(item.balance.interestOutstandingCents, item.loan.currency)} · fees {formatMoney(item.balance.feeOutstandingCents, item.loan.currency)} · penalties {formatMoney(item.balance.penaltyOutstandingCents ?? 0, item.loan.currency)}</p>
    {item.balance.outstandingCents > 0 && <Button fullWidth onClick={onPay} variant="secondary"><Icon name="banknote" />Record repayment</Button>}
    <a {...stylex.props(loanStyles.loanMeta)} href={`/people/${encodeURIComponent(otherID)}`}>View expenses, terms and sharing →</a>
  </article>
}
